import { FC, PropsWithChildren, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Text from "../Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import styles from "./styles.module.css";
import { Group, Menu } from "@mantine/core";
import { NavigationItem } from "@/components/molecules/Navigation";
import { useTranslation } from "react-i18next";
import { useGetCreateOrderValues } from "@/services/Orders";

interface NavItemProps {
  navItem: NavigationItem;
}

const NavItem: FC<PropsWithChildren<NavItemProps>> = ({ navItem }) => {
  const location = useLocation();
  const isActive = location.pathname === navItem.url;
  const { t } = useTranslation();
  const { data: createOrderValues } = useGetCreateOrderValues();
  const [menuOpen, setMenuOpen] = useState(false);

  if (navItem.children && navItem.richDropdown) {
    // A group without a path of its own (e.g. "More") is active when one of its children is.
    const parentActive = navItem.url
      ? location.pathname.startsWith(navItem.url)
      : navItem.children.some((child) => location.pathname === child.url);
    return (
      <li className={styles.navItemRoot}>
        <Menu
          position="bottom-start"
          trigger="hover"
          onOpen={() => setMenuOpen(true)}
          onClose={() => setMenuOpen(false)}
        >
          <Menu.Target>
            <Group gap={5} wrap="nowrap" className={`${styles.link} ${parentActive ? styles.active : ""}`}>
              <Text span display="technical" tt="uppercase" strong={parentActive}>
                {t(navItem.translationsKey)}
                {navItem.superscript && <sup className={styles.taskCount}>{navItem.superscript}</sup>}
              </Text>
              <SVGAsset
                name="chevron-down-white"
                width={10}
                height={10}
                className={menuOpen ? styles.chevronOpen : styles.chevron}
              />
            </Group>
          </Menu.Target>
          <Menu.Dropdown className={styles.richDropdown}>
            {navItem.children.map((child, i) => {
              const fullUrl = `${navItem.url}${child.url}`;
              const childActive = location.pathname === fullUrl;
              return (
                <Link key={i} className={styles.menuLink} to={fullUrl}>
                  <Menu.Item className={`${styles.richItem} ${childActive ? styles.richItemActive : ""}`}>
                    <span className={styles.richItemContent}>
                      <span className={styles.richItemLabelRow}>
                        <Text span display="technical" tt="uppercase" size="sm" strong={childActive}>
                          {t(child.translationsKey)}
                        </Text>
                        {child.count !== undefined && (
                          <span className={`${styles.richCount} ${childActive ? styles.richCountActive : ""}`}>
                            {child.count}
                          </span>
                        )}
                      </span>
                      {child.subLabel && (
                        <Text span display="technical" size="xs" color="tertiary">
                          {child.subLabel}
                        </Text>
                      )}
                    </span>
                    {childActive && <span className={styles.richDot} />}
                  </Menu.Item>
                </Link>
              );
            })}
          </Menu.Dropdown>
        </Menu>
      </li>
    );
  }

  if (navItem.children) {
    return (
      <li className={styles.navItemRoot}>
        <Menu position={"bottom-start"}>
          <Menu.Target>
            <Group
              justify={"space-between"}
              align={"flex-start"}
              className={`${styles.link} ${isActive ? styles.active : ""}`}
            >
              <Text span={true} mr={10} display="technical" tt="uppercase" strong={isActive}>
                {t(navItem.translationsKey)}
              </Text>
              {/*<SVGAsset name={"chevron-down-white"} />*/}
            </Group>
          </Menu.Target>
          <Menu.Dropdown>
            {navItem.children.map((child, i) => {
              if (child.id === "salesReport" || child.id === "buyReport") {
                return (
                  <Menu.Sub key={i}>
                    <Menu.Sub.Target>
                      <Menu.Sub.Item style={{ width: "auto" }}>{t(child.translationsKey)}</Menu.Sub.Item>
                    </Menu.Sub.Target>
                    <Menu.Sub.Dropdown>
                      {createOrderValues?.salesItems.map((item, idx) => (
                        <Link key={idx} className={styles.menuLink} to={`${navItem.url}${child.url}/${item.id}`}>
                          <Menu.Item style={{ width: "auto", textDecoration: "none" }} key={i}>
                            {t(`enumerables:plastic.${item.value}`)}
                          </Menu.Item>
                        </Link>
                      ))}
                    </Menu.Sub.Dropdown>
                  </Menu.Sub>
                );
              } else {
                return (
                  <Link key={i} className={styles.menuLink} to={`${navItem.url}${child.url}`}>
                    <Menu.Item style={{ width: "auto" }} key={i}>
                      {t(child.translationsKey)}
                    </Menu.Item>
                  </Link>
                );
              }
            })}
          </Menu.Dropdown>
        </Menu>
      </li>
    );
  } else {
    return (
      <li className={styles.navItemRoot}>
        <Link to={navItem.url} className={`${styles.link} ${isActive ? styles.active : ""}`}>
          <Text span={true} display="technical" tt="uppercase" strong={isActive}>
            {t(navItem.translationsKey)}
            {navItem.superscript && <sup className={styles.taskCount}>{navItem.superscript}</sup>}
          </Text>
        </Link>
      </li>
    );
  }
};

export default NavItem;
